/**
 * FAQ 生成の縦の導線（見積もり → 確認 → 起動 → 進捗）。
 *
 * この面が守っていること:
 * - 見積もりを見ずに起動できる経路を作らない。開始に渡す対象件数は、見積もりが
 *   返した `targetCount` を**そのまま**渡す（画面が数え直さない）。食い違えば
 *   API が CONFLICT で断り、その断りをそのまま出す。
 * - 進捗を引き直す間隔は `Job.suggestedPollIntervalSec` に従う。画面が秒数を
 *   決め打ちしない。null（終端）になったらポーリングを止める。
 * - 進行中のジョブは、**API に問い合わせて**拾い直す。jobId を端末の中だけに
 *   覚えると、別の端末・別のタブから続きが見えなくなる。
 * - 失敗・対象外・次回送りを黙って数から消さない。
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useMutation, useQuery } from "urql";
import {
  Button,
  Card,
  Disclosure,
  ErrorState,
  LoadingState,
  Meter,
  Tag,
  FaqGenerationEstimateDocument,
  JobProgressDocument,
  RunningJobsDocument,
  StartFaqGenerationDocument,
  type JobEstimateFieldsFragment,
  type JobProgressQuery,
  type JobState,
} from "visumo-data-agent-web-shared";
import { HelpIcon } from "../help/HelpIcon";

import {
  DEFAULT_FAQ_GENERATION_POPULATION,
  MODEL_FAQ_NOTE,
  POPULATION_LABEL,
  STUB_FAQ_NOTE,
} from "../api-capabilities";
import { JobControls } from "./JobControls";

type Job = NonNullable<JobProgressQuery["job"]>;

/** 終端。ここに来たらポーリングを止め、やり直しははじめからにする */
const TERMINAL_STATES: readonly JobState[] = [
  "SUCCEEDED",
  "SUCCEEDED_WITH_ERRORS",
  "FAILED",
  "CANCELLED",
];

function isTerminal(state: JobState): boolean {
  return TERMINAL_STATES.includes(state);
}

function count(value: number | null | undefined): string {
  return value == null ? "—" : value.toLocaleString("ja-JP");
}

/** 1 起動につき 1 つ。再送のたびに作り直すと二重に起動できてしまう */
function newIdempotencyKey(): string {
  return crypto.randomUUID();
}

function seconds(value: number | null): string | null {
  if (value == null) {
    return null;
  }
  if (value < 60) {
    return `およそ ${value} 秒`;
  }
  return `およそ ${Math.round(value / 60)} 分`;
}

/**
 * 見積もりの中身。費用が出せないときは、出せない理由を数字と同じ場所に置く
 * （空欄にすると「0 円」と読めてしまう）。
 */
function EstimateBody({ estimate }: { estimate: JobEstimateFieldsFragment }) {
  const duration = seconds(estimate.estimatedSeconds);
  return (
    <>
      <div className="vd-grid">
        <div>
          <div className="vd-stat-label">生成する商品</div>
          <div className="vd-stat-value">{count(estimate.targetCount)} 件</div>
          <div className="vd-stat-note">
            {estimate.basis.label}
            {estimate.basis.denominator == null
              ? ""
              : `（母数 ${estimate.basis.denominator.toLocaleString("ja-JP")} 件）`}
          </div>
        </div>
        <div>
          <div className="vd-stat-label">AI への問い合わせ</div>
          <div className="vd-stat-value">
            {count(estimate.estimatedLlmCalls)} 回
          </div>
          <div className="vd-stat-note">予算はこの回数で数えています</div>
        </div>
        <div>
          <div className="vd-stat-label">費用の見込み</div>
          <div className="vd-stat-value">
            {estimate.estimatedCost == null
              ? "出せません"
              : `${estimate.estimatedCost.toLocaleString("ja-JP")} ${estimate.estimatedCurrency ?? ""}`.trim()}
          </div>
          <div className="vd-stat-note">
            {estimate.estimatedCost == null
              ? (estimate.costGap?.why ?? "単価の記録がありません")
              : "実行後の請求で確定します"}
          </div>
        </div>
        <div>
          <div className="vd-stat-label">かかる時間</div>
          <div className="vd-stat-value">{duration ?? "—"}</div>
          <div className="vd-stat-note">
            {duration === null ? "実測がまだありません" : "実測からの見込み"}
          </div>
        </div>
      </div>

      {estimate.skippedCount === 0 ? null : (
        <p className="vd-stat-note" style={{ marginBottom: 0 }}>
          このうち {count(estimate.skippedCount)} 件は対象外です（
          {estimate.skippedReasons
            .map((item) => `${item.label} ${item.count} 件`)
            .join(" ・ ")}
          ）。
        </p>
      )}
    </>
  );
}

/** 対象外の内訳。0 件と混ぜずに、理由つきで残す */
function SkippedReasons({
  reasons,
}: {
  reasons: readonly { reason: string; label: string; count: number }[];
}) {
  if (reasons.length === 0) {
    return null;
  }
  return (
    <ul className="vd-job-reasons">
      {reasons.map((item) => (
        <li key={item.reason}>
          {item.label}: {count(item.count)} 件
        </li>
      ))}
    </ul>
  );
}

/** 進捗の面。state ごとに言うことを変える（同じ文言で状態だけ差し替えない） */
function JobView({ job }: { job: Job }) {
  const failedTargets = job.failedTargets.edges;
  const results = (
    <div className="vd-grid">
      <div>
        <div className="vd-stat-label">作れた</div>
        <div className="vd-stat-value">{count(job.succeeded)} 件</div>
      </div>
      <div>
        <div className="vd-stat-label">作れなかった</div>
        <div className="vd-stat-value">{count(job.failed)} 件</div>
      </div>
      <div>
        <div className="vd-stat-label">対象外</div>
        <div className="vd-stat-value">{count(job.skipped)} 件</div>
      </div>
      <div>
        <div className="vd-stat-label">次回に回った</div>
        <div className="vd-stat-value">{count(job.deferred)} 件</div>
      </div>
    </div>
  );

  switch (job.state) {
    case "QUEUED":
      return (
        <div className="vd-job" role="status">
          <div className="vd-card-title">
            待ち行列にいます
            <Tag tone="info">受付</Tag>
          </div>
          <p className="vd-stat-note">
            順番が来ると生成を始めます。この画面を閉じても進みます。
          </p>
          {job.estimate === null ? null : (
            <EstimateBody estimate={job.estimate} />
          )}
        </div>
      );

    case "RUNNING":
      return (
        <div className="vd-job" role="status">
          <div className="vd-card-title">
            FAQ を生成しています（{count(job.succeeded)} / {count(job.total)}）
            <Tag tone="info">実行中</Tag>
          </div>
          <Meter ratio={job.progress} />
          <p className="vd-stat-note">
            {job.progress == null
              ? "進み具合はまだ出せません"
              : `${Math.round(job.progress * 100)} % まで進みました`}
            。この画面を閉じても進みます。
          </p>
        </div>
      );

    case "SUCCEEDED":
      return (
        <div className="vd-job" role="status">
          <div className="vd-card-title">
            FAQ の生成が終わりました
            <Tag tone="ok">完了</Tag>
          </div>
          {results}
          <SkippedReasons reasons={job.skippedReasons} />
        </div>
      );

    case "SUCCEEDED_WITH_ERRORS":
      return (
        <div className="vd-job" role="status">
          <div className="vd-card-title">
            一部の商品で FAQ を作れませんでした
            <Tag tone="warn">一部失敗</Tag>
          </div>
          {results}
          {(job.deferred ?? 0) > 0 ? (
            <p style={{ margin: "8px 0 0" }}>
              予算の上限に達したぶんは次回に回りました。
            </p>
          ) : null}
          <SkippedReasons reasons={job.skippedReasons} />
          {failedTargets.length === 0 ? null : (
            <div style={{ marginTop: 12 }}>
              <Disclosure
                summary={`作れなかった商品が ${count(job.failedTargets.totalCount ?? failedTargets.length)} 件あります`}
              >
                <ul className="vd-job-reasons">
                  {failedTargets.map(({ node }) => (
                    <li key={node.targetId}>
                      {node.label}: {node.reason}
                    </li>
                  ))}
                </ul>
              </Disclosure>
            </div>
          )}
        </div>
      );

    case "FAILED":
      return (
        <div className="vd-job vd-job--error" role="alert">
          <div className="vd-card-title">
            FAQ の生成に失敗しました
            <Tag tone="danger">失敗</Tag>
          </div>
          <p style={{ margin: "4px 0 0" }}>
            {job.errorSummary ?? "理由を受け取れませんでした。"}
          </p>
          {results}
        </div>
      );

    case "CANCELLED":
      return (
        <div className="vd-job" role="status">
          <div className="vd-card-title">
            FAQ の生成は中止されました
            <Tag>中止</Tag>
          </div>
          {results}
        </div>
      );
  }
}

export function FaqGenerationPanel({
  productId,
  onFinished,
}: {
  productId: string;
  /** 終わったら商品詳細を引き直す（作られた FAQ を反映するため） */
  onFinished: () => void;
}) {
  const [confirming, setConfirming] = useState(false);
  const [jobId, setJobId] = useState<string | null>(null);
  const [pollTick, setPollTick] = useState(0);
  // 1 起動につき 1 つ。再送（CONFLICT からのやり直し）では作り直さない
  const idempotencyKey = useRef<string | null>(null);
  const notified = useRef<string | null>(null);

  const target = {
    population: DEFAULT_FAQ_GENERATION_POPULATION,
    productIds: [productId],
  };

  const [estimateResult, refetchEstimate] = useQuery({
    query: FaqGenerationEstimateDocument,
    variables: { input: target },
    pause: !confirming,
    requestPolicy: "network-only" as const,
  });

  // 画面を開き直したときに拾い直すぶん。sessionStorage には置かない
  const [runningResult] = useQuery({
    query: RunningJobsDocument,
    variables: { state: "RUNNING" as JobState, productId },
    pause: jobId !== null,
  });
  const [queuedResult] = useQuery({
    query: RunningJobsDocument,
    variables: { state: "QUEUED" as JobState, productId },
    pause: jobId !== null,
  });

  const [jobResult, refetchJob] = useQuery({
    query: JobProgressDocument,
    variables: { id: jobId ?? "" },
    pause: jobId === null,
    requestPolicy: "network-only" as const,
  });

  const [startResult, startFaqGeneration] = useMutation(
    StartFaqGenerationDocument,
  );

  const job = jobResult.data?.job ?? null;

  /**
   * 拾い直しの問い合わせがまだ解決していない。
   *
   * **この間は起動の導線を出さない。** 出してしまうと、すでに動いている生成が
   * あった場合に「押せる状態 → 進捗」と一瞬で入れ替わる。人は押し損ねるだけだが、
   * ブラウザの通しは `isVisible()` の 1 回で判定するので、解決前の姿を見て
   * 「この商品は起動できる」と読み違える（2026-09-19 に dev で実際に起きた）。
   */
  const lookingUp =
    jobId === null &&
    (runningResult.data === undefined || queuedResult.data === undefined) &&
    runningResult.error === undefined &&
    queuedResult.error === undefined;

  // 動いているものがあれば、押していなくても進捗の面へ戻す
  useEffect(() => {
    if (jobId !== null) {
      return;
    }
    const found =
      runningResult.data?.jobs.edges[0]?.node ??
      queuedResult.data?.jobs.edges[0]?.node ??
      null;
    if (found !== null) {
      setJobId(found.id);
      setConfirming(false);
    }
  }, [jobId, runningResult.data, queuedResult.data]);

  // 次に引くまでの秒数はサーバーが指示する。null なら終端なので止める
  useEffect(() => {
    if (jobId === null || job === null) {
      return;
    }
    const intervalSec = job.suggestedPollIntervalSec;
    if (intervalSec == null || intervalSec <= 0) {
      return;
    }
    const timer = setTimeout(() => {
      setPollTick((tick) => tick + 1);
      refetchJob({ requestPolicy: "network-only" });
    }, intervalSec * 1000);
    return () => clearTimeout(timer);
  }, [jobId, job, pollTick, refetchJob]);

  // 終わったら商品詳細を引き直す。1 つのジョブにつき 1 回だけ
  useEffect(() => {
    if (job === null || !isTerminal(job.state) || notified.current === job.id) {
      return;
    }
    notified.current = job.id;
    onFinished();
  }, [job, onFinished]);

  const start = useCallback(
    async (estimate: JobEstimateFieldsFragment) => {
      idempotencyKey.current ??= newIdempotencyKey();
      const response = await startFaqGeneration({
        input: {
          target,
          // 画面が数え直さず、見積もりが返した件数をそのまま渡す
          acknowledgedTargetCount: estimate.targetCount,
          idempotencyKey: idempotencyKey.current,
        },
      });
      const started = response.data?.startFaqGeneration.job ?? null;
      if (started !== null) {
        setJobId(started.id);
        setConfirming(false);
        idempotencyKey.current = null;
      }
    },
    [productId, startFaqGeneration],
  );

  const reset = (): void => {
    setJobId(null);
    setConfirming(false);
    idempotencyKey.current = null;
    notified.current = null;
  };

  const userErrors = startResult.data?.startFaqGeneration.userErrors ?? [];
  const estimate = estimateResult.data?.faqGenerationEstimate ?? null;

  return (
    <Card
      title="FAQ を生成する"
      note="生成した答えはすぐには配信されません。中身を見てから配信の判断をします。"
      extra={
        <span className="vd-row">
          {jobId === null && !confirming && !lookingUp ? (
            <Button variant="primary" onClick={() => setConfirming(true)}>
              FAQ を生成
            </Button>
          ) : null}
          <HelpIcon slug="products/faq-generation" />
        </span>
      }
    >
      {lookingUp ? (
        <LoadingState label="動いている生成があるか確かめています" />
      ) : jobId !== null ? (
        <div className="vd-stack">
          {jobResult.error !== undefined ? (
            <ErrorState
              title="進捗を読み込めませんでした"
              description={jobResult.error.message}
              onRetry={() => refetchJob({ requestPolicy: "network-only" })}
            />
          ) : job === null ? (
            <LoadingState label="進み具合を読み込んでいます" />
          ) : (
            <>
              <JobView job={job} />
              {/*
                取り消し・やり直しは 1 か所にまとめてある。やり直しでは
                **別のジョブ**が返るので、見る先をそちらへ移す
              */}
              <JobControls
                job={job}
                onChanged={(next) => {
                  if (next.id !== job.id) {
                    notified.current = null;
                  }
                  setJobId(next.id);
                  refetchJob({ requestPolicy: "network-only" });
                }}
              />
              {isTerminal(job.state) ? (
                <div className="vd-row">
                  <Button onClick={reset}>閉じる</Button>
                </div>
              ) : null}
            </>
          )}
        </div>
      ) : !confirming ? (
        <p style={{ margin: 0 }}>
          {POPULATION_LABEL[DEFAULT_FAQ_GENERATION_POPULATION]}
          のうち、この商品について答えを作ります。押すとまず見積もりを出します。
        </p>
      ) : estimateResult.error !== undefined ? (
        <ErrorState
          title="見積もりを出せませんでした"
          description={estimateResult.error.message}
          onRetry={() => refetchEstimate({ requestPolicy: "network-only" })}
        />
      ) : estimate === null ? (
        <LoadingState label="見積もりを出しています" />
      ) : (
        <div className="vd-stack">
          <EstimateBody estimate={estimate} />

          {/*
            起動の前なので `Faq.generator` はまだ無い。代わりに**費用の見込みが
            出せているか**で見分ける — 単価を持つのはモデルにつながった生成器だけで、
            固定応答は必ず `costGap` つきの null を返す(サーバー側で対になっている)。
          */}
          <p className="vd-job-stub">
            {estimate.costGap === null ? MODEL_FAQ_NOTE : STUB_FAQ_NOTE}
          </p>

          {estimate.costGap === null ? null : (
            <Disclosure summary="費用の見込みを出せないのはなぜか">
              <p style={{ margin: 0 }}>
                {estimate.costGap.why}（必要なもの: {estimate.costGap.needs}）
              </p>
            </Disclosure>
          )}

          {userErrors.length === 0 ? null : (
            <div className="vd-state vd-state--error" role="alert">
              <span className="vd-state-title">生成を始められませんでした</span>
              {userErrors.map((error) => (
                <span key={`${error.code}-${error.message}`}>
                  {error.message}
                </span>
              ))}
            </div>
          )}

          <div className="vd-row">
            <Button
              variant="primary"
              disabled={startResult.fetching}
              onClick={() => {
                void start(estimate);
              }}
            >
              {startResult.fetching ? "起動しています" : "この内容で生成する"}
            </Button>
            <Button onClick={() => setConfirming(false)}>やめる</Button>
          </div>
        </div>
      )}
    </Card>
  );
}
